
import React from 'react';
import { LegalPageLinks } from '@/components/legal/LegalPageLinks';
import { Helmet } from 'react-helmet-async';
import Footer from "@/components/Footer";

const Privacy = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Privacy Policy | How Fly Collects and Protects Your Data</title>
        <meta name="description" content="Read the Fly privacy policy to see what personal data we collect, how we use and store it, who we share it with, and the choices you have over it." />
        <meta name="keywords" content="privacy policy, data protection, personal information, data security" />
        <link rel="canonical" href="https://www.fly-social.com/privacy-policy" />
      </Helmet>
      {/* Stripped header: Fly logo only, no nav links. Absolute rather than fixed, so on a
          phone it scrolls away instead of sitting on top of the text. */}
      <div className="absolute top-0 left-0 right-0 z-50 px-6 lg:px-10 pt-6 pointer-events-none">
        <a href="/" className="inline-flex items-center pointer-events-auto" aria-label="Fly home">
          <img
            src="/lovable-uploads/2e7bf5aa-9c7b-41ea-b64a-48b87f3dc01d.png"
            alt="Fly"
            className="h-16 md:h-20 drop-shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
          />
        </a>
      </div>
      
      {/* One centred reading column, about 75 characters at prose-lg. */}
      <div className="container mx-auto px-5 pt-28 md:pt-36 pb-16">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-gray-600 mb-4">Last updated: 02 Nov 26</p>

          <div className="prose prose-lg prose-slate max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Introduction</h2>
            <p>
              At Fly, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Information We Collect</h2>
            <p>
              <strong>Personal Information:</strong> We collect information that you provide directly to us when registering for an account, using our services, or contacting our support team. This may include your name, email address, phone number, business information, and payment details.
            </p>
            <p>
              <strong>Usage Information:</strong> We automatically collect certain information about your device and how you interact with our platform, including your IP address, browser type, referring/exit pages, and operating system.
            </p>
            <p>
              <strong>How you found us and what you looked at:</strong> We record the pages of fly-social.com you visit, the page and link that brought you to us (including campaign tags and ad click identifiers added to links by Google, Meta and others), the device and browser you use, and roughly where you are. When you sign up, we keep this with your account so we know which pages and campaigns led to it.
            </p>
            <p>
              <strong>How you use the Fly Agent app:</strong> When you are signed in to the Fly Agent app, we record which pages of the app you open and how long each one is on screen, linked to your account, so our team can see where people get stuck and help them.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">3. How We Use Your Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze trends, usage, and activities in connection with our platform</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              <li>Personalize and improve your experience on our platform</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Sharing Your Information</h2>
            <p>
              We do not sell your personal information to third parties. We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Service providers who perform services on our behalf</li>
              <li>Business partners with whom we jointly offer products or services</li>
              <li>Legal authorities when required by law or to protect our rights and safety</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Analytics, Session Recording and Cookies</h2>
            <p>
              We use these tools to understand how people use our website and the Fly Agent app, to find what confuses them, and to measure our advertising:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                <strong>Microsoft Clarity</strong> records how visitors use our pages: clicks, scrolling, the pages opened and, on our sign-up pages, what is typed (never passwords or one-time codes). In the Fly Agent app it hides typed text, numbers and email addresses, and we hide the details of your own customers (see section 6). Recordings can be watched for 30 days. See the{' '}
                <a href="https://privacy.microsoft.com/privacystatement" className="text-violet-700 hover:underline" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a>.
              </li>
              <li>
                <strong>Google Analytics and Google Tag Manager</strong> measure visits and ad results on our public pages. See{' '}
                <a href="https://policies.google.com/privacy" className="text-violet-700 hover:underline" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
              </li>
              <li>
                <strong>Meta Pixel and the X (Twitter) pixel</strong> measure visits that come from our ads on those platforms. See{' '}
                <a href="https://www.facebook.com/privacy/policy" className="text-violet-700 hover:underline" target="_blank" rel="noopener noreferrer">Meta's Privacy Policy</a>.
              </li>
              <li>
                <strong>Crisp</strong>, our support chat, keeps the messages you send us and basic details about your browser.
              </li>
              <li>
                <strong>Sentry</strong> receives a technical report when something on our pages breaks (the page, the browser and the steps just before the error), so we can fix it.
              </li>
            </ul>
            <p>
              These tools and our own pages use cookies and your browser's local storage to keep you signed in, recognise a returning browser, and remember how you first found us. Pages that carry a private link or name your business (owner links and app addresses) never load Google, Meta or X tools.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Your Customers' Data</h2>
            <p>
              When you connect your Google Business Profile and use the Fly Agent, we handle information about your own customers on your behalf: reviews and the names of reviewers, messages, contacts, leads and bookings. We use it only to provide the service to you. We never sell it, never use it for our own advertising, and hide it from our session recordings.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Your Choices</h2>
            <ul className="list-disc pl-6 mb-4">
              <li>You can block cookies or use a browser setting or extension that stops analytics. Our website and app keep working.</li>
              <li>If you are in the European Union, the United Kingdom or Switzerland, Microsoft Clarity does not set its cookies without your consent.</li>
              <li>We keep the record of how you found us and the pages you used for as long as your account exists, and delete it with your account when you ask (section 8).</li>
              <li>To ask what we hold about you, or to correct it, email <a href="mailto:fly@zoomlocal.in" className="text-violet-700 hover:underline">fly@zoomlocal.in</a>.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Data Deletion Requests</h2>
            <p>
              You have the right to request deletion of your personal data. To request data deletion:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Email Request:</strong> Send an email to <a href="mailto:fly@zoomlocal.in" className="text-violet-700 hover:underline">fly@zoomlocal.in</a> with the subject line "Data Deletion Request"</li>
              <li><strong>Include Information:</strong> Please include your account email address and specify which data you want deleted</li>
              <li><strong>Verification:</strong> We may ask you to verify your identity before processing the deletion request</li>
              <li><strong>Processing Time:</strong> We will process your request within 30 days of verification</li>
              <li><strong>Confirmation:</strong> You will receive confirmation once your data has been deleted</li>
            </ul>
            <p>
              Please note that some information may need to be retained for legal compliance, security purposes, or to complete transactions you have requested.
            </p>
            
          </div>

          <LegalPageLinks current="/privacy-policy" />
        </div>
      </div>
      
      <Footer compact />
    </div>
  );
};

export default Privacy;
